import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootStateProps } from '@/state/store.ts';
import { setAuth, logOut } from '@/state/reducers/auth.slice';
import AuthService from '@/lib/auth';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import biometricAuthService from '@/services/biometricAuthService';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { decodeToken } from '@/lib/api';

// List of public paths that don't require authentication
const PUBLIC_PATHS = [
  '/login',
  '/auth/register',
  '/auth/forgot-password',
  '/verify-email',
  /^\/q\/[a-zA-Z0-9]+$/, // Public questionnaire access
  /^\/public\/.*$/, // All public routes
  /^\/questionnaire\/t\/.*$/, // Token-based questionnaire access
  /^\/visitor\/id\/.*$/, // Visitor routes
];

// Helper function to check if current path is public
const isPublicPath = (pathname: string): boolean => {
  return PUBLIC_PATHS.some(publicPath => {
    if (typeof publicPath === 'string') {
      return pathname.startsWith(publicPath);
    }
    return publicPath.test(pathname);
  });
};

export default function useAuth() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasRestoredAuth, setHasRestoredAuth] = useState(false);
  const auth = useSelector((state: RootStateProps) => state.auth);
  const dispatch = useDispatch();
  const { refetchHospitals } = useLocationContext();

  const location = useLocation();

  // Check if current route is public
  const checkIsPublicRoute = useCallback(() => {
    return isPublicPath(location.pathname);
  }, [location.pathname]);

  // Restore auth state on app load or route change
  useEffect(() => {
    let isMounted = true;
    let isRestoring = false;

    const restoreAuthState = async () => {
      // Skip auth check for public routes
      if (checkIsPublicRoute()) {
        console.log('🔓 Public route detected, skipping auth check');
        if (isMounted) {
          setIsLoading(false);
          setHasRestoredAuth(true);
        }
        return;
      }

      if (hasRestoredAuth) return; // Prevent multiple restorations

      try {
        console.log('🔄 Restoring auth state (Cognito)...');

        // Check for current authenticated user in Cognito
        let currentUser;
        try {
          currentUser = await getCurrentUser();
        } catch (err) {
          console.log('❌ No valid Cognito session');
          localStorage.removeItem('jwt_token');
          dispatch(logOut());
          if (isMounted) {
            setIsLoading(false);
            setHasRestoredAuth(true);
          }
          return;
        }

        const session = await fetchAuthSession();
        const userAttributes = await fetchUserAttributes();
        const accessToken = session.tokens?.accessToken?.toString() || '';
        const idToken = session.tokens?.idToken?.toString() || '';
        
        console.log('🔍 Cognito Attributes:', userAttributes);
        console.log('🔍 ID Token Payload:', decodeToken(idToken)?.payload);

        console.log('✅ Valid Cognito session found:', currentUser.username);

        // TOKEN EXCHANGE CHECK: Ensure we have a backend-trusted token
        let backendToken = localStorage.getItem('jwt_token');

        if (backendToken === accessToken || backendToken === idToken) {
          backendToken = null;
        }

        // If no backend token exists, or it's a generic Admin token, we perform the exchange
        const decoded = backendToken ? decodeToken(backendToken) : null;
        const isAdminToken = decoded?.payload?.sub === "admin-MnTrHealthAdmin";

        if (!backendToken || isAdminToken) {
          console.log(isAdminToken ? '🔄 Admin token detected, performing token exchange...' : '🔄 No backend token found, performing token exchange...');
          try {
            // IMPORTANT: Using user.username as it represents the migrated auth_uuid/identifier
            const result = await biometricAuthService.login(currentUser.username, true);
            if (result.success && result.data?.access_token) {
              backendToken = result.data.access_token;
              console.log('✅ Token exchange successful');
            } else {
              console.error('❌ Token exchange failed: No token in response');
            }
          } catch (err) {
            console.error('❌ Token exchange failed:', err);
          }
        }

        // Fetch user data from backend (RDS via API)
        const email = userAttributes.email;
        let userData: { id?: string; name?: string; email?: string; role?: string; staff_role?: string; auth_uuid?: string; associated_branch?: string; hospital_name?: string } | null = null;

        if (email) {
          try {
            userData = await AuthService.getProfile(email);
          } catch (err) {
            console.warn('⚠️ Profile fetch failed', err);
            userData = null;
          }
        }

        if (!userData) {
          console.warn('⚠️ Could not fetch staff_users data by email, falling back to attributes');

          let effectiveRole = userAttributes['custom:role'] || null;
          const fallbackName = userAttributes.name || email?.split('@')[0] || 'User';

          // 3. Last Resort: Emergency recovery from Name (for unblocking dev/admin users)
          if (!effectiveRole && userAttributes.name) {
            const nameLower = userAttributes.name.toLowerCase();
            if (nameLower.includes('branch admin')) {
              effectiveRole = 'BranchAdmin';
              console.warn('⚠️ Emergency Role Recovery: Inferred BranchAdmin role from name');
            } else if (nameLower.includes('super admin') || nameLower.includes('admin')) {
              effectiveRole = 'Admin';
              console.warn('⚠️ Emergency Role Recovery: Inferred Admin role from name');
            }
          }

          // Determine clinical status from metadata
          let isClinical = false;
          try {
            const roleMetadata = await AuthService.getRolesMetadata();
            const roleInfo = roleMetadata.find((r: any) => 
              (r.role && effectiveRole && r.role.toLowerCase() === effectiveRole.toLowerCase())
            );
            isClinical = roleInfo ? !!roleInfo.is_clinical : false;
          } catch (err) {
            console.error('Error fetching role metadata:', err);
          }

          dispatch(setAuth({
            token: backendToken || accessToken, // Prefer backend token
            user: {
              id: currentUser.username, // Use username as ID
              username: currentUser.username,
              email: email || '',
              name: fallbackName,
              role: effectiveRole,
              isClinical
            },
            expiresIn: 3600
          }));
        } else {
          // Success case - use data from RDS
          console.log('✅ User data restored from DB:', userData);
          let isClinical = false;
          try {
            const roleMetadata = await AuthService.getRolesMetadata();
            const roleInfo = roleMetadata.find((r: any) => 
              (r.role && userData.role && r.role.toLowerCase() === userData.role.toLowerCase())
            );
            isClinical = roleInfo ? !!roleInfo.is_clinical : false;
          } catch (err) {
            console.error('Error fetching role metadata:', err);
          }

          dispatch(setAuth({
            token: backendToken || accessToken,
            user: {
              id: userData.auth_uuid || currentUser.username,
              username: currentUser.username,
              email: userData.email || email || '',
              name: userData.name || userAttributes.name || 'User',
              role: userData.role || userData.staff_role,
              isClinical,
              associated_branch: userData.associated_branch,
              hospital_name: userData.hospital_name
            },
            expiresIn: 3600
          }));
        }

        refetchHospitals();

      } catch (error) {
        console.error('❌ Error restoring auth state:', error);
        localStorage.removeItem('jwt_token');
        dispatch(logOut());
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setHasRestoredAuth(true);
        }
      }
    };

    // Only run the auth state restoration if we haven't already done so
    if (!hasRestoredAuth && !isRestoring) {
      isRestoring = true;
      restoreAuthState();
    }

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [dispatch, hasRestoredAuth, checkIsPublicRoute]);

  // Listen for auth state changes from Amplify Hub
  useEffect(() => {
    const hubListener = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      console.log('🔔 Amplify auth event:', event);

      if (event === 'signedOut') {
        localStorage.removeItem('jwt_token');
        dispatch(logOut());
      } else if (event === 'signedIn') {
        console.log('✅ Auth session updated - User Signed In');
        // Trigger a re-fetch or reload if needed, or simply let the app routing handle it
        setHasRestoredAuth(false); // Force re-check
      }
    });

    return () => {
      hubListener();
    };
  }, [dispatch]);

  // Set loading to false after auth restoration is complete
  useEffect(() => {
    if (hasRestoredAuth && auth.token !== undefined) {
      setIsLoading(false);
    }
  }, [auth, hasRestoredAuth]);

  return { ...auth, isLoading };
}